import type { TranscriptTurn } from '@acs/shared';
import { base64ToBytes, bytesToBase64, CAPTURE_WORKLET, decodeMulaw, PCMU_RATE, Upsampler } from './pcmu.js';

/**
 * The browser test/demo call on the gateway's media socket (2026-10-15) — the
 * browser plays the part a phone carrier plays, in the same frame dialect
 * (Twilio Media Streams: `start`, `media`, `mark`, `clear`, `stop`, 8 kHz
 * μ-law), so the gateway runs the very session a phone call runs: same engine,
 * prompts, tools, read-backs and call record.
 *
 * What a carrier does that this class does:
 *
 * - streams the microphone as 20 ms μ-law frames, continuously;
 * - plays what it is sent in order, and echoes each `mark` when playback
 *   actually REACHES it — the gateway's read-back consent depends on that;
 * - on `clear` drops everything unplayed and returns the pending marks at once
 *   (the session tells a flushed mark from a played one by its generation).
 *
 * In addition the gateway sends `turn` (the call's transcript, by index) and
 * `tool` (a finished tool call) frames, for the page to show.
 */
export interface MediaSocketCallDeps {
  url: string;
  streamSid: string;
  context: AudioContext;
  mic: MediaStream;
  /** Where the assistant's voice is played — an <audio> element. */
  output: HTMLAudioElement | null;
  onOpen: () => void;
  onTurn: (index: number, turn: TranscriptTurn) => void;
  onTool: (name: string, args: unknown, result: unknown) => void;
  /** The socket closed — the gateway ended the call, or it failed. */
  onClose: (info: { code: number; reason: string; started: boolean }) => void;
  log?: (msg: string, detail?: unknown) => void;
}

/** Audio scheduled this far ahead of "now" at the earliest (jitter cushion). */
const LEAD_S = 0.06;

export class MediaSocketCall {
  private ws: WebSocket | null = null;
  private capture: AudioWorkletNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private readonly sink: GainNode;
  private readonly destination: MediaStreamAudioDestinationNode;
  private readonly upsampler: Upsampler;
  private playhead = 0;
  private readonly playing = new Set<AudioBufferSourceNode>();
  private readonly marks: { name: string; timer: ReturnType<typeof setTimeout> }[] = [];
  private loopback: { a: RTCPeerConnection; b: RTCPeerConnection } | null = null;
  private started = false;
  private closed = false;

  constructor(private readonly d: MediaSocketCallDeps) {
    this.sink = d.context.createGain();
    this.destination = d.context.createMediaStreamDestination();
    this.sink.connect(this.destination);
    this.upsampler = new Upsampler(PCMU_RATE, d.context.sampleRate);
  }

  /** The assistant's voice as a stream (for the wave), before any routing. */
  get remoteStream(): MediaStream {
    return this.destination.stream;
  }

  async start(): Promise<void> {
    const { context } = this.d;
    if (context.state === 'suspended') await context.resume().catch(() => undefined);
    await this.routeOutput();
    const url = URL.createObjectURL(new Blob([CAPTURE_WORKLET], { type: 'application/javascript' }));
    try {
      await context.audioWorklet.addModule(url);
    } finally {
      URL.revokeObjectURL(url);
    }
    if (this.closed) return;

    const ws = new WebSocket(this.d.url);
    this.ws = ws;
    ws.onopen = () => {
      if (this.closed) return;
      this.send({ event: 'connected', protocol: 'Call', version: '1.0.0' });
      this.send({ event: 'start', start: { streamSid: this.d.streamSid, mediaFormat: { encoding: 'audio/x-mulaw', sampleRate: PCMU_RATE, channels: 1 } } });
      this.started = true;
      this.startCapture();
      this.d.onOpen();
    };
    ws.onmessage = (e) => this.onMessage(e.data);
    ws.onclose = (e) => this.finish(e.code, e.reason);
    ws.onerror = () => this.d.log?.('media socket error');
  }

  /** The owner ended the call: tell the gateway, which finalises it. */
  hangup(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send({ event: 'stop', streamSid: this.d.streamSid });
      this.ws.close(1000, 'caller hung up');
    }
    this.teardown();
  }

  private onMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let m: { event?: string; media?: { payload?: string }; mark?: { name?: string }; index?: number; turn?: TranscriptTurn; name?: string; args?: unknown; result?: unknown };
    try {
      m = JSON.parse(raw);
    } catch {
      return;
    }
    switch (m.event) {
      case 'media':
        if (m.media?.payload) this.play(m.media.payload);
        break;
      case 'mark':
        if (m.mark?.name) this.queueMark(m.mark.name);
        break;
      case 'clear':
        this.clear();
        break;
      case 'turn':
        if (typeof m.index === 'number' && m.turn) this.d.onTurn(m.index, m.turn);
        break;
      case 'tool':
        if (m.name) this.d.onTool(m.name, m.args, m.result);
        break;
      default:
        break;
    }
  }

  private play(payload: string): void {
    const { context } = this.d;
    const samples = this.upsampler.push(decodeMulaw(base64ToBytes(payload)));
    if (samples.length === 0) return;
    const buffer = context.createBuffer(1, samples.length, context.sampleRate);
    buffer.getChannelData(0).set(samples);
    const node = context.createBufferSource();
    node.buffer = buffer;
    node.connect(this.sink);
    const at = Math.max(this.playhead, context.currentTime + LEAD_S);
    node.start(at);
    this.playhead = at + buffer.duration;
    this.playing.add(node);
    node.onended = () => this.playing.delete(node);
  }

  /** Echo the mark when everything queued before it has been heard. */
  private queueMark(name: string): void {
    const wait = Math.max(0, this.playhead - this.d.context.currentTime);
    const entry = {
      name,
      timer: setTimeout(() => {
        const i = this.marks.indexOf(entry);
        if (i >= 0) this.marks.splice(i, 1);
        this.sendMark(name);
      }, wait * 1000),
    };
    this.marks.push(entry);
  }

  /** Barge-in: drop the unplayed audio, return pending marks at once (as a carrier does). */
  private clear(): void {
    for (const node of this.playing) {
      try {
        node.stop();
      } catch {
        /* already ended */
      }
    }
    this.playing.clear();
    this.playhead = 0;
    for (const m of this.marks.splice(0)) {
      clearTimeout(m.timer);
      this.sendMark(m.name);
    }
  }

  private sendMark(name: string): void {
    this.send({ event: 'mark', streamSid: this.d.streamSid, mark: { name } });
  }

  private startCapture(): void {
    const { context, mic } = this.d;
    this.source = context.createMediaStreamSource(mic);
    this.capture = new AudioWorkletNode(context, 'pcmu-capture');
    this.capture.port.onmessage = (e: MessageEvent<Uint8Array>) => {
      this.send({ event: 'media', streamSid: this.d.streamSid, media: { payload: bytesToBase64(e.data) } });
    };
    this.source.connect(this.capture);
    // A worklet only runs while connected to the graph; its output is silence.
    const mute = context.createGain();
    mute.gain.value = 0;
    this.capture.connect(mute).connect(context.destination);
  }

  /**
   * Play the voice through a local WebRTC loopback: Chrome's echo canceller
   * only subtracts audio that came in over WebRTC, and without it the
   * assistant would hear itself through the speakers and stop mid-sentence.
   * Falls back to the plain stream when the loopback cannot be set up.
   */
  private async routeOutput(): Promise<void> {
    const out = this.d.output;
    if (!out) return;
    let stream: MediaStream = this.destination.stream;
    try {
      const a = new RTCPeerConnection();
      const b = new RTCPeerConnection();
      a.onicecandidate = (e) => e.candidate && void b.addIceCandidate(e.candidate).catch(() => undefined);
      b.onicecandidate = (e) => e.candidate && void a.addIceCandidate(e.candidate).catch(() => undefined);
      const received = new Promise<MediaStream>((resolve) => {
        b.ontrack = (e) => resolve(e.streams[0] ?? new MediaStream([e.track]));
      });
      for (const track of this.destination.stream.getAudioTracks()) a.addTrack(track, this.destination.stream);
      const offer = await a.createOffer();
      await a.setLocalDescription(offer);
      await b.setRemoteDescription(offer);
      const answer = await b.createAnswer();
      await b.setLocalDescription(answer);
      await a.setRemoteDescription(answer);
      stream = await Promise.race([received, new Promise<MediaStream>((_, rej) => setTimeout(() => rej(new Error('loopback timeout')), 3_000))]);
      this.loopback = { a, b };
    } catch (err) {
      this.d.log?.('echo-cancelling loopback unavailable — playing directly', err);
      this.loopback?.a.close();
      this.loopback?.b.close();
      this.loopback = null;
    }
    out.srcObject = stream;
    await out.play().catch(() => undefined);
  }

  private send(frame: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(frame));
  }

  private finish(code: number, reason: string): void {
    const wasClosed = this.closed;
    this.teardown();
    if (!wasClosed) this.d.onClose({ code, reason, started: this.started });
  }

  private teardown(): void {
    if (this.closed) return;
    this.closed = true;
    for (const m of this.marks.splice(0)) clearTimeout(m.timer);
    for (const node of this.playing) {
      try {
        node.stop();
      } catch {
        /* already ended */
      }
    }
    this.playing.clear();
    if (this.capture) this.capture.port.onmessage = null;
    this.capture?.disconnect();
    this.source?.disconnect();
    this.loopback?.a.close();
    this.loopback?.b.close();
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) this.ws.close();
    if (this.d.output) this.d.output.srcObject = null;
  }
}
